import { Box } from '@mui/material';
import { keyframes } from '@mui/material/styles';

import RadarChart, { type RadarChartDataSeries } from '@/components/common/RadarChart';

// ─── Keyframes ────────────────────────────────────────────────────────────────

export const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const radarPulse = keyframes`
  0%, 100% { opacity: 0.25; filter: drop-shadow(0 0 6px var(--pulse-color, rgba(90,233,255,0.3))); }
  50% { opacity: 0.55; filter: drop-shadow(0 0 14px var(--pulse-color, rgba(90,233,255,0.5))); }
`;

// ─── ShimmerLine ──────────────────────────────────────────────────────────────

interface ShimmerLineProps {
  width?: string | number;
  height?: string | number;
  borderRadius?: number;
  /** Base color for the gradient — defaults to dark-on-dark */
  baseColor?: string;
  highlightColor?: string;
  style?: React.CSSProperties;
  delay?: number;
}

export const ShimmerLine = ({
  width = '100%',
  height = 14,
  borderRadius = 6,
  baseColor = 'rgba(255,255,255,0.04)',
  highlightColor = 'rgba(255,255,255,0.10)',
  style,
  delay = 0,
}: ShimmerLineProps) => (
  <Box
    sx={{
      width,
      height,
      borderRadius: `${borderRadius}px`,
      background: `linear-gradient(90deg, ${baseColor} 25%, ${highlightColor} 50%, ${baseColor} 75%)`,
      backgroundSize: '200% 100%',
      animation: `${shimmer} 1.5s ease-in-out infinite`,
      animationDelay: `${delay}s`,
      flexShrink: 0,
      ...style,
    }}
  />
);

// ─── ShimmerValue ─────────────────────────────────────────────────────────────

interface ShimmerValueProps {
  width?: number;
  height?: number;
  borderRadius?: number;
  style?: React.CSSProperties;
}

export const ShimmerValue = ({
  width = 48,
  height = 20,
  borderRadius = 6,
  style,
}: ShimmerValueProps) => (
  <ShimmerLine
    width={width}
    height={height}
    borderRadius={borderRadius}
    style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}
  />
);

// ─── ShimmerBlock ─────────────────────────────────────────────────────────────

interface ShimmerBlockProps {
  lines?: number;
  baseColor?: string;
  highlightColor?: string;
  style?: React.CSSProperties;
}

const LINE_WIDTHS = ['100%', '92%', '78%', '88%', '65%'];

export const ShimmerBlock = ({
  lines = 4,
  baseColor,
  highlightColor,
  style,
}: ShimmerBlockProps) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, ...style }}>
    {Array.from({ length: lines }).map((_, i) => (
      <ShimmerLine
        key={i}
        width={LINE_WIDTHS[i % LINE_WIDTHS.length]}
        height={12}
        baseColor={baseColor}
        highlightColor={highlightColor}
        delay={i * 0.1}
      />
    ))}
  </div>
);

// ─── LoadingRadarChart ────────────────────────────────────────────────────────

interface LoadingRadarChartProps {
  labels: string[];
  accentColor: string;
  size?: number;
}

export const LoadingRadarChart = ({
  labels,
  accentColor,
  size = 500,
}: LoadingRadarChartProps) => {
  const placeholderSeries: RadarChartDataSeries[] = [
    {
      data: labels.map((label) => ({ label, value: 50 })),
      strokeColor: accentColor,
      fillColor: `${accentColor}18`,
      strokeWidth: 2,
    },
  ];

  // No labels means no reserved margin around the hexagon — scale the whole
  // placeholder up so it doesn't look small relative to the labeled chart it's standing in for.
  const hexagonSize = size * 1.3;

  return (
    <Box
      sx={{
        '--pulse-color': `${accentColor}60`,
        animation: `${radarPulse} 2.4s ease-in-out infinite`,
        pointerEvents: 'none',
      } as React.CSSProperties & Record<string, string>}
    >
      <RadarChart
        series={placeholderSeries}
        size={hexagonSize}
        gridColor="rgba(255,255,255,0.1)"
        gridLevels={4}
        showLabels={false}
      />
    </Box>
  );
};
